import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaDownload,
  FaFilePdf,
  FaChevronDown,
  FaChevronUp,
  FaUser,
  FaTag,
  FaQuestionCircle,
  FaCheckCircle,
  FaExclamationCircle,
} from 'react-icons/fa';
import { Spinner } from 'react-bootstrap';
import {
  buildSection91ReportState,
  buildSection91PrintDocumentHtml,
  groupSection91Rows,
  section91RowHasAnswer,
} from './section91ReportUtils';
import { downloadDomAsA4Pdf } from './section91DomToA4Pdf';

const Section91Report = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [exporting, setExporting] = useState(false);
  const [downloadingA4, setDownloadingA4] = useState(false);
  const [expandedSection91, setExpandedSection91] = useState(true);

  const searchParams = new URLSearchParams(location.search);
  const [selectedCustomer, setSelectedCustomer] = useState(searchParams.get('customer') || '');
  const [selectedTopic, setSelectedTopic] = useState(searchParams.get('topic') || '');
  const [selectedField, setSelectedField] = useState(searchParams.get('field') || '');
  const [companyAdmins, setCompanyAdmins] = useState([]);

  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(() => buildSection91ReportState([], t, i18n));

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setSelectedCustomer(params.get('customer') || '');
    setSelectedTopic(params.get('topic') || '');
    setSelectedField(params.get('field') || '');
  }, [location.search]);

  useEffect(() => {
    const userRole = localStorage.getItem('role');
    if (userRole === 'superAdmin') {
      fetchCompanyAdmins();
    } else if (userRole === 'companyAdmin') {
      const currentUserId = localStorage.getItem('userId');
      if (currentUserId && !selectedCustomer) {
        setSelectedCustomer(currentUserId);
      }
    }
  }, []);

  useEffect(() => {
    fetchReportsData();
  }, [selectedCustomer, selectedTopic, selectedField]);

  const fetchCompanyAdmins = async () => {
    try {
      const response = await axios.get('company-admins');
      if (response.data.status) {
        setCompanyAdmins(response.data.companyAdmins || []);
      }
    } catch (error) {
      console.error('Error fetching company admins:', error);
    }
  };

  const fetchReportsData = async () => {
    const userRole = localStorage.getItem('role');

    if (userRole === 'superAdmin' && !selectedCustomer) {
      setLoading(false);
      setReportData(buildSection91ReportState([], t, i18n));
      return;
    }

    setLoading(true);
    try {
      const reportsParams = new URLSearchParams();

      if (selectedCustomer) {
        reportsParams.append('userId', selectedCustomer);
      } else if (userRole === 'companyAdmin') {
        const currentUserId = localStorage.getItem('userId');
        if (currentUserId) {
          reportsParams.append('userId', currentUserId);
        }
      }

      if (selectedTopic) {
        reportsParams.append('topic', selectedTopic);
      }
      if (selectedField) {
        reportsParams.append('field', selectedField);
      }

      const reportsResponse = await axios.get(`reports?${reportsParams.toString()}`);

      if (reportsResponse.data.status) {
        const reportsData = reportsResponse.data.reports || [];
        setReportData(buildSection91ReportState(reportsData, t, i18n));
      } else {
        setReportData(buildSection91ReportState([], t, i18n));
      }
    } catch (error) {
      console.error('Error fetching Section 9.1 report data:', error);
      toastr.error(t('reporting.failedToLoadReportData'));
      setReportData(buildSection91ReportState([], t, i18n));
    } finally {
      setLoading(false);
    }
  };

  const section91Grouped = useMemo(
    () => groupSection91Rows(reportData.section91Disclosures || []),
    [reportData.section91Disclosures]
  );

  const exportToPDF = async () => {
    setExporting(true);
    try {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toastr.error(t('reporting.popupBlocked'));
        return;
      }

      const htmlContent = buildSection91PrintDocumentHtml(reportData, t);
      printWindow.document.write(htmlContent);
      printWindow.document.close();

      setTimeout(() => {
        printWindow.print();
        toastr.success(t('reporting.pdfDownloadInitiated'));
      }, 500);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toastr.error(t('reporting.failedToExportPDF'));
    } finally {
      setExporting(false);
    }
  };

  const downloadPdfA4 = async () => {
    setExpandedSection91(true);
    setDownloadingA4(true);
    await new Promise((r) => setTimeout(r, 350));
    try {
      const root = document.querySelector('.section91-report-print-root');
      if (!root) {
        toastr.error(t('reporting.couldNotFindReportContent'));
        return;
      }
      await downloadDomAsA4Pdf(root, reportData.clientName);
      toastr.success(t('reporting.pdfA4Saved'));
    } catch (error) {
      console.error('Error generating A4 PDF:', error);
      toastr.error(t('reporting.failedToExportPDF'));
    } finally {
      setDownloadingA4(false);
    }
  };

  const superAdminNoCustomer =
    localStorage.getItem('role') === 'superAdmin' && !selectedCustomer;
  const canCapturePdf = !loading && !superAdminNoCustomer;

  return (
    <div className="min-h-screen max-sm:w-[100vw] bg-gray-50 font-sans antialiased">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4">
        <div className="bg-white rounded-lg shadow border border-gray-100 p-3 mb-4">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => navigate('/reporting')}
                className="px-3 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-2 text-sm"
              >
                <FaChevronDown className="w-3 h-3 rotate-90" />
                {t('reporting.back')}
              </button>

              {localStorage.getItem('role') === 'superAdmin' && (
                <div className="min-w-[200px]">
                  <select
                    className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                    value={selectedCustomer}
                    onChange={(e) => {
                      setSelectedCustomer(e.target.value);
                      const params = new URLSearchParams(location.search);
                      if (e.target.value) {
                        params.set('customer', e.target.value);
                      } else {
                        params.delete('customer');
                      }
                      navigate(`/section-91-report?${params.toString()}`, { replace: true });
                    }}
                  >
                    <option value="">--Select Customer--</option>
                    {companyAdmins.map((admin) => (
                      <option key={admin.id} value={admin.id}>
                        {admin.companyName || admin.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="relative flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={downloadPdfA4}
                  disabled={downloadingA4 || exporting || !canCapturePdf}
                  className="px-3 py-2 bg-emerald-700 text-white rounded hover:bg-emerald-800 flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {downloadingA4 ? (
                    <Spinner animation="border" size="sm" className="mr-2" />
                  ) : (
                    <FaFilePdf className="w-3 h-3" />
                  )}
                  {downloadingA4 ? t('reporting.exporting') : t('reporting.downloadPdfA4')}
                </button>
                <button
                  type="button"
                  onClick={exportToPDF}
                  disabled={exporting || downloadingA4}
                  className="px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {exporting ? (
                    <Spinner animation="border" size="sm" className="mr-2" />
                  ) : (
                    <FaDownload className="w-3 h-3" />
                  )}
                  {exporting ? t('reporting.exporting') : t('reporting.exportToPdf')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Spinner animation="border" variant="success" style={{ width: '3rem', height: '3rem' }} />
            <p className="mt-4 text-gray-600 text-lg">{t('reporting.loadingReportData')}</p>
          </div>
        ) : localStorage.getItem('role') === 'superAdmin' && !selectedCustomer ? (
          <div className="flex flex-col items-center justify-center py-20">
            <FaUser className="w-16 h-16 text-gray-400 mb-4" />
            <p className="text-gray-600 text-lg font-semibold">{t('reporting.selectCustomerSection91')}</p>
          </div>
        ) : (
          <div className="space-y-6 section91-report-print-root">
            <section className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-center py-8 px-4">
                <h1 className="text-2xl sm:text-3xl font-bold mb-4 underline decoration-white/30">
                  {t('reporting.sustainabilityDisclosureReport')}
                </h1>
                <div className="mt-6 space-y-2">
                  <h2 className="text-xl sm:text-2xl font-semibold">{reportData.clientName}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl mx-auto text-sm">
                    <p>
                      <strong>{t('reporting.sasbSector')}:</strong> {reportData.sasbSector}
                    </p>
                    <p>
                      <strong>{t('reporting.sasbIndustry')}:</strong> {reportData.sasbIndustry}
                    </p>
                    <p>
                      <strong>{t('reporting.reportingPeriod')}:</strong> {reportData.reportingPeriod}
                    </p>
                    <p>
                      <strong>{t('reporting.reportDate')}:</strong> {reportData.reportDate}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
              <div
                role="button"
                tabIndex={0}
                onClick={() => setExpandedSection91((v) => !v)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setExpandedSection91((v) => !v);
                  }
                }}
                className="p-4 cursor-pointer hover:bg-gray-50 flex items-center justify-between"
              >
                <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                  <FaTag className="w-4 h-4 mr-2 text-teal-600" />
                  {t('reporting.section91Title')}
                </h2>
                <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center">
                  {expandedSection91 ? (
                    <FaChevronUp className="w-2.5 h-2.5 text-gray-600" />
                  ) : (
                    <FaChevronDown className="w-2.5 h-2.5 text-gray-600" />
                  )}
                </div>
              </div>

              {expandedSection91 && (
                <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-6">
                  {(reportData.section91Disclosures || []).length === 0 ? (
                    <p className="text-gray-600 italic">{t('reporting.section91ReportEmpty')}</p>
                  ) : (
                    section91Grouped.map((dimensionData, dimIndex) => (
                      <div
                        key={`section91-dim-${dimIndex}-${dimensionData.dimensionName}`}
                        className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm"
                      >
                        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 border-b border-gray-200">
                          <h3 className="text-lg font-semibold text-gray-800 mb-2">
                            {`9.1.${dimIndex + 1} ${t('reporting.sustainabilityDimension')}: ${dimensionData.dimensionName}`}
                          </h3>
                          <p className="text-gray-600 text-sm italic">
                            {t('reporting.section91DimensionDefinition', { dimension: dimensionData.dimensionName })}
                          </p>
                        </div>

                        <div className="p-4 space-y-6">
                          {dimensionData.items.map((row) => {
                            const req = row.completedRequirements;
                            const hasAns = section91RowHasAnswer(row);
                            const reasonDisplay = (() => {
                              const r = row.noAnswerReason;
                              if (r == null) return '—';
                              const s = String(r).trim();
                              if (s === '' || s === '—') return '—';
                              return s;
                            })();
                            return (
                              <div
                                key={row.rowKey}
                                className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50/40"
                              >
                                {req && (
                                  <div className="p-4 border-b border-gray-200 bg-white">
                                    <p className="font-semibold text-gray-800 mb-3">{t('reporting.completedRequirements')}:</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-3 text-sm">
                                      <div className="bg-blue-50 p-2 rounded border border-blue-200 min-w-0">
                                        <p className="font-semibold text-blue-800">{t('reporting.sustainabilityDimension')}:</p>
                                        <p className="text-blue-700 font-medium break-words [overflow-wrap:anywhere]">
                                          {dimensionData.dimensionName}
                                        </p>
                                      </div>
                                      <div className="bg-cyan-50 p-2 rounded border border-cyan-200 min-w-0">
                                        <p className="font-semibold text-cyan-900">{t('reporting.disclosureTopicLabel')}:</p>
                                        <p className="text-xs font-semibold text-cyan-900 mt-0.5 break-words [overflow-wrap:anywhere] leading-snug whitespace-normal">
                                          {req.disclosureFieldTitle || '—'}
                                        </p>
                                      </div>
                                      <div className="bg-amber-50 p-2 rounded border border-amber-200 min-w-0">
                                        <p className="font-semibold text-amber-900">{t('reporting.sasbAuditCodeLabel')}:</p>
                                        <p className="text-amber-800 break-words [overflow-wrap:anywhere]">{req.sasbAuditCode}</p>
                                      </div>
                                      <div className="bg-indigo-50 p-2 rounded border border-indigo-200 min-w-0">
                                        <p className="font-semibold text-indigo-900">{t('reporting.section91ColReportingPeriod')}:</p>
                                        <p className="text-indigo-950 break-words [overflow-wrap:anywhere] leading-snug">
                                          {row.reportingPeriod}
                                        </p>
                                      </div>
                                      <div className="bg-violet-50 p-2 rounded border border-violet-200 min-w-0">
                                        <p className="font-semibold text-violet-900">{t('reporting.cmfNcg30461Article')}</p>
                                        <p className="text-violet-800 mt-0.5 break-words [overflow-wrap:anywhere] leading-snug whitespace-pre-wrap">
                                          {req.ncg30461Article}
                                        </p>
                                      </div>
                                      <div className="bg-purple-50 p-2 rounded border border-purple-200 min-w-0">
                                        <p className="font-semibold text-purple-900">{t('reporting.cmfNcg30519Article')}</p>
                                        <p className="text-purple-900 mt-0.5 break-words [overflow-wrap:anywhere] leading-snug whitespace-pre-wrap">
                                          {req.ncg30519Article}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm items-stretch">
                                      <div className="bg-teal-50 p-3 rounded-lg border border-teal-200 min-w-0 flex flex-col">
                                        <p className="font-semibold text-teal-900 mb-1">{t('reporting.section91ColSasbMetric')}</p>
                                        <p className="text-teal-950 break-words [overflow-wrap:anywhere] leading-snug whitespace-pre-wrap grow">
                                          {row.sasbMetricName}
                                        </p>
                                      </div>
                                      <div className="bg-green-50 p-3 rounded-lg border border-green-200 min-w-0 flex flex-col">
                                        <p className="font-semibold text-green-800 mb-1">{t('reporting.cmfRequirements')}</p>
                                        <p className="text-green-800 break-words [overflow-wrap:anywhere] whitespace-pre-wrap leading-relaxed grow">
                                          {req.cmfRequirements}
                                        </p>
                                      </div>
                                      <div className="bg-sky-50 p-3 rounded-lg border border-sky-200 min-w-0 flex flex-col">
                                        <p className="font-semibold text-sky-900 mb-1">{t('reporting.section91ColValueUnits')}</p>
                                        <p className="text-sky-950 break-words [overflow-wrap:anywhere] grow">{row.valueUnits}</p>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                <div className="p-4 border-b border-gray-100 bg-white">
                                  <p className="font-semibold text-gray-800 mb-2 flex items-start">
                                    <FaQuestionCircle className="w-4 h-4 mr-2 text-teal-600 mt-0.5 flex-shrink-0" />
                                    {t('reporting.question')}
                                  </p>
                                  <p className="text-gray-800 leading-relaxed whitespace-pre-wrap pl-6 md:pl-7">
                                    {row.engagementQuestion}
                                  </p>
                                </div>

                                <div className="p-4 border-b border-gray-100 bg-white space-y-4">
                                  {hasAns ? (
                                    <div className="border-l-4 border-green-500 pl-4 bg-green-50/60 rounded-r-lg py-3 pr-3">
                                      <p className="font-semibold text-gray-800 mb-2 flex items-start">
                                        <FaCheckCircle className="w-4 h-4 mr-2 text-green-600 mt-0.5 flex-shrink-0" />
                                        {t('reporting.answer')}
                                      </p>
                                      <p className="text-gray-800 whitespace-pre-wrap ml-6 md:ml-7">{row.answer}</p>
                                    </div>
                                  ) : (
                                    <div className="border-l-4 border-amber-500 pl-4 bg-amber-50/60 rounded-r-lg py-3 pr-3">
                                      <p className="font-semibold text-gray-800 mb-2 flex items-start">
                                        <FaExclamationCircle className="w-4 h-4 mr-2 text-amber-600 mt-0.5 flex-shrink-0" />
                                        {t('reporting.section91ColNoAnswerReason')}
                                      </p>
                                      <p className="text-gray-800 whitespace-pre-wrap ml-6 md:ml-7">{reasonDisplay}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default Section91Report;
